import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Layout from "components/Layout";
import EmphasisText from "components/EmphasisText";
import LeonardoLanguageToggle from "components/LeonardoLanguageToggle";
import api from "utils/api";
import { useLeonardoLocale } from "scenes/leonardoNikolai/LeonardoLocaleContext";
import { createTranslator } from "scenes/leonardoNikolai/i18n";
import { normalizePreferredLanguage } from "utils/preferredLanguage";
import { setLogin } from "state";
import "styles/auth.css";

const LEONARDO_NIKOLAI_SOURCE = "leonardo-nikolai";
const LEONARDO_NIKOLAI_PATH = "/leonardo-nikolai";

const LEONARDO_NYX_SOURCE = "leonardo-nyx";

const LEONARDO_NYX_PATH = "/leonardo-nyx";

const isSafeReturnPath = (path) =>
  Boolean(path && path.startsWith("/") && !path.startsWith("//"));

const isLeonardoUser = (user) => {
  const source = String(user?.source || "").trim().toLowerCase();
  if (source === LEONARDO_NIKOLAI_SOURCE || source === LEONARDO_NYX_SOURCE) return true;
  const path = String(user?.registrationPath || "").trim().toLowerCase();
  return (
    path === LEONARDO_NIKOLAI_PATH ||
    path.startsWith(`${LEONARDO_NIKOLAI_PATH}/`) ||
    path === LEONARDO_NYX_PATH ||
    path.startsWith(`${LEONARDO_NYX_PATH}/`)
  );
};

const resolveLeonardoPath = (returnNext, user) => {
  if (isSafeReturnPath(returnNext) && returnNext.startsWith(LEONARDO_NYX_PATH)) {
    return LEONARDO_NYX_PATH;
  }

  const source = String(user?.source || "").trim().toLowerCase();
  if (source === LEONARDO_NYX_SOURCE) return LEONARDO_NYX_PATH;

  const path = String(user?.registrationPath || "").trim().toLowerCase();
  if (path === LEONARDO_NYX_PATH || path.startsWith(`${LEONARDO_NYX_PATH}/`)) {
    return LEONARDO_NYX_PATH;
  }

  return LEONARDO_NIKOLAI_PATH;
};

const resolveVerifyDestination = ({ returnNext, isLeonardoFlow }) => {
  if (
    isSafeReturnPath(returnNext) &&
    !returnNext.startsWith("/buchen/erfolg")
  ) {
    return returnNext;
  }
  if (isLeonardoFlow) return LEONARDO_NIKOLAI_PATH;
  return "/konto";
};

const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const returnNext = params.get("next") || "";
  const isEmailChange =
    params.get("zweck") === "email-aendern" || params.get("purpose") === "email-change";
  const { shellT, isLeonardoFlow } = useLeonardoLocale();
  const [status, setStatus] = useState({
    kind: "loading",
    message: isEmailChange
      ? shellT("shell.auth.verify.loadingEmailChange")
      : shellT("shell.auth.verify.loading"),
  });
  const [leonardoVerified, setLeonardoVerified] = useState(false);

  const [leonardoReturnPath, setLeonardoReturnPath] = useState(LEONARDO_NIKOLAI_PATH);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus({
        kind: "error",
        message: shellT("shell.auth.verify.incomplete"),
      });
      return;
    }
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const endpoint = isEmailChange ? "/auth/verify-email-change" : "/auth/verify-email";
        const { data } = await api.get(endpoint, { params: { token } });
        dispatch(setLogin({ token: data.token, user: data.user }));

        const leonardo = isLeonardoUser(data.user) || isLeonardoFlow;
        setLeonardoVerified(leonardo);
        setLeonardoReturnPath(resolveLeonardoPath(returnNext, data.user));

        const messageT = leonardo
          ? createTranslator(normalizePreferredLanguage(data.user?.preferredLanguage))
          : shellT;

        const successMessage =
          data.activatedBookings > 0
            ? messageT("shell.auth.verify.successBooking")
            : isEmailChange
              ? messageT("shell.auth.verify.successEmailChange")
              : messageT("shell.auth.verify.success");

        setStatus({
          kind: "success",
          message: successMessage,
        });

        if (!leonardo) {
          const destination = resolveVerifyDestination({
            returnNext,
            isLeonardoFlow: leonardo,
          });
          window.setTimeout(() => {
            navigate(destination, { replace: true });
          }, 1500);
        }
      } catch (err) {
        setStatus({
          kind: "error",
          message:
            err?.response?.data?.message || shellT("shell.auth.verify.invalid"),
        });
      }
    })();
  }, [
    token,
    isEmailChange,
    dispatch,
    navigate,
    returnNext,
    isLeonardoFlow,
    shellT,
  ]);

  const showLeonardoUi = isLeonardoFlow || leonardoVerified;

  return (
    <Layout>
      <section className="auth-page">
        <div className="auth-card">
          {showLeonardoUi && (
            <div className="auth-lang-toggle-row">
              <LeonardoLanguageToggle />
            </div>
          )}
          <div className="auth-kicker">{shellT("shell.auth.verify.kicker")}</div>
          <h1>
            <EmphasisText text={shellT("shell.auth.verify.title")} />
          </h1>
          <div className={`auth-alert is-${status.kind === "loading" ? "info" : status.kind}`}>
            {status.message}
          </div>
          <div className="auth-links">
            {showLeonardoUi && status.kind === "success" ? (
              <Link to={leonardoReturnPath}>
                {shellT("shell.auth.verify.backToLeonardo")}
              </Link>
            ) : (
              <Link to="/einloggen">{shellT("shell.auth.verify.backToLogin")}</Link>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default VerifyEmailPage;

